import { Identifier } from '@/state/signify'
import { utilAgentService } from '@/utils/apis'

/**
 * Strictly sanitizes an alias.
 *
 * - Removes diacritics (e.g. é → e)
 * - Replaces whitespace with hyphens
 * - Removes all non-alphanumeric characters except hyphen
 * - Collapses multiple hyphens
 * - Trims leading and trailing hyphens
 *
 * Only allows: A–Z, a–z, 0–9, and hyphen (-)
 *
 * @param input - Raw alias input
 * @returns A URL-safe, normalized alias string
 */
export const sanitizeAlias = (input: string): string => {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Sanitizes an alias into a lowercase, hyphen-separated slug.
 *
 * - Lowercases the input
 * - Replaces spaces and common punctuation (.,?!)
 * - Collapses multiple hyphens
 * - Trims leading and trailing hyphens
 *
 * @param input - Raw string input
 * @returns A lowercase, hyphenated string suitable for alias prefill
 */
export const slugifyAlias = (input: string): string => {
  return input
    .toLowerCase()
    .replace(/[.,?!\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function calculateEqualMultisigWeightage(totalMembers: number) {
  let weightages = Array.from({ length: totalMembers }, () => ({
    numerator: 1,
    denominator: totalMembers
  }))

  return weightages
}

export const shortenAid = (aid: string): string => {
  if (!aid) return ''

  const prefixLength = aid.length <= 44 ? 4 : 8
  return aid.slice(0, prefixLength) + '...' + aid.slice(-4)
}

export const sortAids = (aids: string[]) => {
  let sortedAids = aids.sort(compareAids)
  return sortedAids
}

export const compareAids = (a: string, b: string) => {
  if (a < b) {
    return -1
  }
  if (a > b) {
    return 1
  }
  return 0
}

export async function findOrgGroupAidByType(
  type: string,
  identifiers: Identifier[],
  orgName: string
): Promise<Identifier | undefined> {
  try {
    let orgGroupAidAlias: string | undefined = undefined

    if (type === 'legal-entity') {
      orgGroupAidAlias = (
        await utilAgentService.constructAlias({
          role: 'legal-entity',
          org: orgName,
          isMultisigParticipant: false, // todo Sasa speak
          isOrgAid: true
        })
      )?.alias
    } else if (type === 'qvi') {
      orgGroupAidAlias = (
        await utilAgentService.constructAlias({
          role: 'qvi',
          org: orgName,
          isMultisigParticipant: false, // todo Sasa speak
          isOrgAid: true
        })
      )?.alias
    }

    if (!orgGroupAidAlias) return undefined

    const needle = orgGroupAidAlias.trim().toUpperCase()
    const groupAid = identifiers.find(
      (i) => i.isMultisig === true && i.name.toUpperCase().includes(needle)
    )
    return groupAid
    // Returns undefined on any lookup error; callers handle falsy return as "not found".
  } catch (ex) {
    return undefined
  }
}

export function isGroupAid(aid: any): boolean {
  return (
    aid.hasOwnProperty('group') &&
    typeof aid.group === 'object' &&
    aid.group !== null
  )
}

export function getLocalMemberFromGroupAid(aid: any):
  | {
      name: string
      prefix: string
    }
  | undefined {
  if (!isGroupAid(aid)) return undefined

  if (
    aid.group.hasOwnProperty('mhab') &&
    typeof aid.group.mhab === 'object' &&
    aid.group.mhab !== null
  ) {
    return {
      name: aid.group.mhab.name,
      prefix: aid.group.mhab.prefix
    }
  }
  return undefined
}

export function getLocalStateFromGroupAid(aid: any): any {
  if (
    aid.hasOwnProperty('group') &&
    typeof aid.group === 'object' &&
    aid.group !== null
  ) {
    if (
      aid.group.hasOwnProperty('mhab') &&
      typeof aid.group.mhab === 'object' &&
      aid.group.mhab !== null
    ) {
      return aid.group.mhab.state
    }
  }
  return null
}

export function getGroupSigningMemberAids(
  groupMembers: any,
  sort: boolean = false
): any {
  let smids = groupMembers['signing'].map((m: { aid: string }) => m.aid)
  if (sort === true) smids = sortAids(smids)
  return smids
}

export function getFirstWitnessFromEnds(ends: any):
  | {
      prefix: string
      http: string
    }
  | undefined {
  if (ends && ends.witness) {
    const witnessAids = Object.keys(ends.witness)
    if (witnessAids?.length > 0) {
      return { prefix: witnessAids[0], http: ends.witness[witnessAids[0]].http }
    }
  }
  return undefined
}
